import type { NotificationResponseDto } from '@/lib/models';

/**
 * Two or more notifications tied to the same market, threaded under one card.
 * `items` keeps the feed order (newest first); the thread itself sits at its
 * newest item's position in the feed.
 */
export interface MarketThread {
  kind: 'thread';
  /** Shared `meta.externalMarketId` — the thread's identity. */
  marketId: string;
  /** First non-empty `meta.marketName` across the thread's items. */
  marketName?: string;
  /** First non-empty `meta.marketImageUrl` across the thread's items. */
  imageUrl?: string;
  items: NotificationResponseDto[];
}

export interface SingleEntry {
  kind: 'single';
  item: NotificationResponseDto;
}

export type FeedEntry = MarketThread | SingleEntry;

function metaString(meta: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = meta?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Threads a recency bucket's rows by market: notifications sharing a
 * `meta.externalMarketId` collapse into one {@link MarketThread} anchored at
 * the newest item's feed position, so related lifecycle events (fill →
 * cancel → resolution) read as one story instead of scattered cards. Rows
 * without a market id — account/broadcast types — and lone market rows stay
 * as singles.
 */
export function groupByMarket(items: NotificationResponseDto[]): FeedEntry[] {
  const countByMarket = new Map<string, number>();
  for (const item of items) {
    const marketId = metaString(item.meta, 'externalMarketId');
    if (marketId) countByMarket.set(marketId, (countByMarket.get(marketId) ?? 0) + 1);
  }

  const entries: FeedEntry[] = [];
  const threadByMarket = new Map<string, MarketThread>();

  for (const item of items) {
    const marketId = metaString(item.meta, 'externalMarketId');
    if (!marketId || (countByMarket.get(marketId) ?? 0) < 2) {
      entries.push({ kind: 'single', item });
      continue;
    }
    let thread = threadByMarket.get(marketId);
    if (!thread) {
      thread = { kind: 'thread', marketId, items: [] };
      threadByMarket.set(marketId, thread);
      entries.push(thread);
    }
    thread.items.push(item);
    thread.marketName ??= metaString(item.meta, 'marketName');
    thread.imageUrl ??= metaString(item.meta, 'marketImageUrl');
  }

  return entries;
}
